// Card writes that keep users/{uid}/meta/counter in step with the cards collection.
//
// firestore.rules (v2.0_15) can't count a collection, so it enforces the tier cap through
// this counter: a card create must bump it +1 and a delete must drop it -1 IN THE SAME
// TRANSACTION. Never write a card with a bare setDoc/deleteDoc — the rules will reject it.
import { collection, doc, getDoc, setDoc, deleteDoc, runTransaction, getCountFromServer } from 'firebase/firestore';

const counterRef = (db, uid) => doc(db, 'users', uid, 'meta', 'counter');
const cardRef = (db, uid, cardId) => doc(db, 'users', uid, 'cards', cardId);

// Legacy accounts (and brand-new ones) have no counter yet. Seed it once from the real
// card count. A concurrent seed from another device is harmless — the transaction below
// re-reads the counter, so a failed duplicate seed is ignored.
export async function ensureCardCounter(db, uid) {
  const ref = counterRef(db, uid);
  if ((await getDoc(ref)).exists()) return;
  // Keep the snapshot intact: data() relies on `this`, so don't destructure it.
  const countSnap = await getCountFromServer(collection(db, 'users', uid, 'cards'));
  try {
    await setDoc(ref, { cardCount: countSnap.data().count, lastCardId: '' });
  } catch (err) {
    if (!(await getDoc(ref)).exists()) throw err;
  }
}

// Create a card and bump the counter atomically. `firestoreCard` must already carry
// createdBy + serverTimestamp() createdTime (create rule). Idempotent: a card that is
// already in Firestore is left alone, so retries never double-count.
export async function createCardWithCounter(db, uid, firestoreCard) {
  await ensureCardCounter(db, uid);
  const cRef = counterRef(db, uid);
  const kRef = cardRef(db, uid, firestoreCard.id);
  await runTransaction(db, async (tx) => {
    const counterSnap = await tx.get(cRef);
    const cardSnap = await tx.get(kRef);
    if (cardSnap.exists()) return;
    const count = counterSnap.exists() ? counterSnap.data().cardCount : 0;
    tx.set(kRef, firestoreCard);
    tx.set(cRef, { cardCount: count + 1, lastCardId: firestoreCard.id });
  });
}

// Delete a card and drop the counter atomically. No counter yet => plain delete (the rules
// allow it for unseeded legacy accounts). Idempotent: deleting a missing card is a no-op.
export async function deleteCardWithCounter(db, uid, cardId) {
  const cRef = counterRef(db, uid);
  const kRef = cardRef(db, uid, cardId);
  await runTransaction(db, async (tx) => {
    const counterSnap = await tx.get(cRef);
    const cardSnap = await tx.get(kRef);
    if (!cardSnap.exists()) return;
    tx.delete(kRef);
    if (counterSnap.exists()) {
      tx.set(cRef, { cardCount: Math.max(0, counterSnap.data().cardCount - 1), lastCardId: cardId });
    }
  });
}

// Account deletion: remove every card one at a time (each delete must move the counter by
// exactly 1), then the counter itself — the rules only allow deleting it once it reads 0.
export async function wipeCardsAndCounter(db, uid, cardIds) {
  for (const id of cardIds) {
    await deleteCardWithCounter(db, uid, id);
  }
  const ref = counterRef(db, uid);
  const snap = await getDoc(ref);
  if (snap.exists() && snap.data().cardCount === 0) await deleteDoc(ref);
}
